import * as React from 'react'
import { ExternalLink, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  lookupCustomer,
  type ProductRequest,
  type ShopifyCustomerInfo,
} from '@/hooks/useProductRequests'
import { fmtDate } from '@/lib/dates'

// There's no Sheet primitive in components/ui, so this is a plain fixed drawer
// with its own overlay — same visual language as the rest of the app (white
// panel, --line border, text-xs density).

function fmtMoney(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n)
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-[11px] uppercase tracking-[0.06em] text-[var(--mute)]">
        {label}
      </span>
      <span className="text-xs text-[var(--text-primary)]">{value}</span>
    </div>
  )
}

export function CustomerSidebar({
  request,
  onClose,
}: {
  request: ProductRequest | null
  onClose: () => void
}) {
  const [loading, setLoading] = React.useState(false)
  const [customer, setCustomer] = React.useState<ShopifyCustomerInfo | null>(null)

  const email = request?.customer_email?.trim() ?? ''

  // Fresh lookup every open: the stored shopify_customer_id was captured when
  // the request was logged, and their order count has moved on since.
  React.useEffect(() => {
    if (!request) {
      setCustomer(null)
      setLoading(false)
      return
    }
    if (!email) {
      setCustomer(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setCustomer(null)
    lookupCustomer(email)
      .then((res) => {
        if (cancelled) return
        setCustomer(res.found && res.customer ? res.customer : null)
      })
      .catch(() => {
        if (!cancelled) setCustomer(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [request, email])

  if (!request) return null

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-label={`Customer detail for ${request.customer_name}`}
        className="brand-theme fixed inset-y-0 right-0 z-50 flex w-[340px] flex-col border-l border-[var(--line-warm)] bg-[var(--surface-card)] shadow-lg"
      >
        <div className="flex items-center gap-2 border-b border-[var(--line-warm)] px-4 py-3">
          <h2 className="text-sm font-semibold text-[var(--text-primary)]">Customer</h2>
          <div className="flex-1" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-[var(--mute)] hover:text-[var(--text-primary)]"
            onClick={onClose}
            aria-label="Close customer panel"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {/* Shopify history */}
          <div className="mb-4">
            <div className="mb-2 border-b border-[var(--line-warm)] pb-1 text-xs uppercase tracking-[0.06em] text-[var(--mute)]">
              Shopify
            </div>
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-36" />
              </div>
            ) : customer ? (
              <>
                <div className="text-sm font-semibold text-[var(--text-primary)]">
                  {customer.display_name || customer.email}
                </div>
                <div className="mt-2">
                  <Row label="Orders" value={customer.orders_count} />
                  <Row label="Total spend" value={fmtMoney(customer.total_spent)} />
                  <Row
                    label="Last order"
                    value={
                      customer.last_order_at ? fmtDate(customer.last_order_at) : '—'
                    }
                  />
                </div>
                <a
                  href={customer.admin_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-xs text-[var(--brand)] hover:underline"
                >
                  Open in Shopify
                  <ExternalLink className="h-3 w-3" />
                </a>
              </>
            ) : (
              <div className="text-xs text-[var(--mute)]">No Shopify match</div>
            )}
          </div>

          {/* What the request itself recorded — always shown, match or not. */}
          <div>
            <div className="mb-2 border-b border-[var(--line-warm)] pb-1 text-xs uppercase tracking-[0.06em] text-[var(--mute)]">
              On this request
            </div>
            <Row label="Name" value={request.customer_name || '—'} />
            <Row label="Email" value={request.customer_email || '—'} />
            <Row label="Phone" value={request.customer_phone || '—'} />
            <Row label="Product" value={request.product_title || '—'} />
            <Row label="Qty" value={request.qty} />
            <Row label="Logged" value={fmtDate(request.created_at) || '—'} />
          </div>
        </div>
      </aside>
    </>
  )
}
